import React, { useMemo } from 'react';
import {
  BankDepositIcon,
  BillingCheckIcon,
  Box,
  Inscription,
  PayPalIcon,
  QuestionIcon,
  ReceiptVaultIcon,
  Text,
  VenmoIcon,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { PaymentMethod, PaymentMethodType } from '@/modules/employee/employee.types';
import DebitPaymentMethodIcon from '@/modules/employee/Employee/PersonalInformation/PaymentMethodInfo/PaymentMethodLabel/DebitPaymentMethodIcon';
import spacings from '@/styles/spacings';
import { getFullAddress } from '@/utils';

const StyledBox = styled(Box)`
  ${({ theme }) => theme.border.blueGray2}
  border-radius: ${({ theme }) => theme.rounds.container2Round} ;
`;

const StyledContentBox = styled(Box)`
  overflow-wrap: break-word;
`;

interface Props {
  isVerifiedDateVisible?: boolean;
  paymentMethod?: Partial<PaymentMethod>;
  simplifyDesc?: boolean;
  accountNumberLast4?: string;
  hasAccountVerificationPendingMessage?: boolean;
  paymentMethodAddress?: string;
}

export const PaymentMethodLabel: React.FC<Props> = ({
  paymentMethod,
  simplifyDesc,
  accountNumberLast4,
  isVerifiedDateVisible = true,
  hasAccountVerificationPendingMessage,
  paymentMethodAddress,
}) => {
  const currentIcon = useMemo(() => {
    const { type, requisites } = paymentMethod || {};

    if (paymentMethodAddress) {
      return <ReceiptVaultIcon data-testid="PaymentMethodLabel_icon_payment-method-address" size={spacings.l} color="iconAccent" />;
    }

    switch (type) {
    case PaymentMethodType.PAYPAL:
      return <PayPalIcon data-testid={`PaymentMethodLabel_icon_${type}`} />;
    case PaymentMethodType.VENMO:
      return <VenmoIcon data-testid={`PaymentMethodLabel_icon_${type}`} />;
    case PaymentMethodType.CHECK:
      return (
        <BillingCheckIcon
          data-testid={`PaymentMethodLabel_icon_${type}`}
          size={spacings.l}
          color="iconAccent"
        />
      );
    case PaymentMethodType.DEBIT:
      return (
        <DebitPaymentMethodIcon cardType={requisites?.cardType} />
      );
    case PaymentMethodType.DIRECT_DEPOSIT:
      return (
        <BankDepositIcon
          data-testid={`PaymentMethodLabel_icon_${type}`}
          size={spacings.l}
          color="iconAccent"
        />
      );
    default:
      return (
        <QuestionIcon
          data-testid="PaymentMethodLabel_no_icon"
          size={spacings.l}
          color="iconAccent"
        />
      );
    }
  }, [paymentMethod, paymentMethodAddress]);

  const currentContent = useMemo(() => {
    const {
      type,
      // accountNumber,
      accountDescription,
      isVerified,
    } = paymentMethod || {};

    if (paymentMethodAddress) {
      return (
        <Box data-testid="PaymentMethodLabel_content_payment-method-address">
          <Text size={spacings.m} color="textTitle">
            {paymentMethodAddress}
          </Text>
        </Box>
      );
    }

    if (!paymentMethod) {
      return (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          <Text size={spacings.m} color="textTitle">
            No payment method added
          </Text>
        </Box>
      );
    }
  
    switch (type) {
    case PaymentMethodType.PAYPAL:
      return (
        <StyledContentBox data-testid={`PaymentMethodLabel_content_${type}`}>
          {paymentMethod.requisites?.email && (
            <Text size={spacings.m} color="textTitle">
              {paymentMethod.requisites?.email}
            </Text>
          )}
          {paymentMethod.requisites?.phoneNumber && (
            <Text
              size={paymentMethod.requisites?.email ? spacings.s : spacings.m}
              color={paymentMethod.requisites?.email ? 'textTitle' : 'textSecondary'}
              weight={500}
            >
              {paymentMethod.requisites?.phoneNumber}
            </Text>
          )}
        </StyledContentBox>
      );
    case PaymentMethodType.VENMO:
      return paymentMethod.requisites?.phoneNumber && (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          <Text size={spacings.m} color="textTitle">
            {paymentMethod.requisites?.phoneNumber}
          </Text>
        </Box>
      );
    case PaymentMethodType.DEBIT:
      return (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          <Text size={spacings.m} color="textTitle">
            {paymentMethod.requisites?.cardType} (•••• {paymentMethod.requisites?.cardLast4})
          </Text>
        </Box>
      );
    case PaymentMethodType.DIRECT_DEPOSIT:
      return (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          <Text size={spacings.m} color="textTitle">
            {`${accountDescription} ${simplifyDesc
              ? `(ends ${accountNumberLast4 || '****'})`
              : `(Bank a/c number ends ${accountNumberLast4 || '****'})`}`}
          </Text>
          {isVerified && isVerifiedDateVisible ? (
            <Box direction="row" gap={spacings.xxxs}>
              <Text size={spacings.s} color="textSecondary" weight={500}>
                Verified on
              </Text>
              <Text size={spacings.s} color="success" weight={500}>
                06/28/21
              </Text>
            </Box>
          ) : null}
          {hasAccountVerificationPendingMessage ? (
            <Inscription color="warning" lineHeight="20px" margin={{ top: 'spacing2'}}>
              Account verification pending
            </Inscription>
          ) : null}
        </Box>
      );
    case PaymentMethodType.CHECK:
      return (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          <Text size={spacings.m} color="textTitle">
            {getFullAddress(paymentMethod?.address)}
          </Text>
        </Box>
      );

    default:
      return null;
    }
  }, [paymentMethod, simplifyDesc, accountNumberLast4, isVerifiedDateVisible, hasAccountVerificationPendingMessage, paymentMethodAddress]);

  return (
    <Box
      data-testid="PaymentMethodLabel_container"
      direction="row"
      align="center"
    >
      <StyledBox
        pad={spacings.xxs}
        margin={{ right: spacings.s }}
        height={{ min: '40px', max: '40px' }}
        width={{ min: '40px', max: '40px' }}
        align="center"
        justify="center"
        data-testid="PaymentMethodLabel_icon"
      >
        {currentIcon}
      </StyledBox>

      {currentContent}
    </Box>
  );
};
